import React, { useMemo } from 'react';

const DonutChart = ({
    data = [],
    size = 160,
    strokeWidth = 30,
    labelKey = 'label',
    valueKey = 'value',
    showLegend = true,
    showTotal = true,
    totalLabel = 'Total',
    className = ''
}) => {
    const chartData = useMemo(() => {
        const total = data.reduce((sum, item) => sum + (item[valueKey] || 0), 0);
        const radius = (size - strokeWidth) / 2;
        const circumference = radius * 2 * Math.PI;

        let currentAngle = 0;
        const segments = data.map((item, index) => {
            const value = item[valueKey] || 0;
            const percentage = total > 0 ? (value / total) * 100 : 0;
            const segmentLength = (percentage / 100) * circumference;
            const offset = circumference - segmentLength;
            const rotation = currentAngle;
            currentAngle += (percentage / 100) * 360;

            return {
                ...item,
                percentage,
                segmentLength,
                offset,
                rotation,
                color: item.color || getDefaultColor(index)
            };
        });

        return { segments, total, radius, circumference };
    }, [data, valueKey, size, strokeWidth]);

    const getDefaultColor = (index) => {
        const colors = [
            '#6366f1', // primary
            '#22c55e', // green
            '#f59e0b', // amber
            '#ef4444', // red
            '#8b5cf6', // purple
            '#06b6d4', // cyan
            '#ec4899', // pink
            '#84cc16', // lime
        ];
        return colors[index % colors.length];
    };

    if (!data.length) {
        return (
            <div className={`flex items-center justify-center text-gray-400 ${className}`} style={{ height: size }}>
                No data available
            </div>
        );
    }

    return (
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 ${className}`}>
            {/* Chart */}
            <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
                <svg width={size} height={size} className="transform -rotate-90">
                    {/* Background circle */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={chartData.radius}
                        fill="none"
                        stroke="#f3f4f6"
                        strokeWidth={strokeWidth}
                    />

                    {/* Segments */}
                    {chartData.segments.map((segment, index) => (
                        <circle
                            key={index}
                            cx={size / 2}
                            cy={size / 2}
                            r={chartData.radius}
                            fill="none"
                            stroke={segment.color}
                            strokeWidth={strokeWidth}
                            strokeDasharray={`${segment.segmentLength} ${chartData.circumference}`}
                            strokeDashoffset={0}
                            transform={`rotate(${segment.rotation} ${size / 2} ${size / 2})`}
                            className="transition-all duration-500 ease-out"
                        />
                    ))}
                </svg>

                {/* Center content */}
                {showTotal && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold text-gray-900 truncate px-2 max-w-full">
                            {chartData.total.toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-500 truncate max-w-full px-2" title={totalLabel}>{totalLabel}</span>
                    </div>
                )}
            </div>

            {/* Legend */}
            {showLegend && (
                <div className="flex flex-col gap-2 w-full sm:w-auto min-w-[140px]">
                    {chartData.segments.map((segment, index) => (
                        <div key={index} className="flex items-center gap-2">
                            <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: segment.color }}
                            />
                            <span className="text-sm text-gray-600 truncate flex-1 min-w-[60px]" title={segment[labelKey]}>
                                {segment[labelKey]}
                            </span>
                            <span className="text-sm font-medium text-gray-900 ml-2 whitespace-nowrap">
                                {segment[valueKey]}
                            </span>
                            <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">
                                ({Math.round(segment.percentage)}%)
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DonutChart;
